import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, Ban, Clock3, DoorOpen, Mail, ShieldCheck, ShieldX } from "lucide-react";
import { WhatsAppButton } from "@/components/WhatsAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cleanRef = (value) => {
  let v = (value || "").trim();
  const match = v.match(/ref=([A-Za-z0-9-]+)/);
  if (match) v = match[1];
  return v.replace(/^TTD:/i, "").toUpperCase();
};

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

export default function VerifyDesk() {
  const [params] = useSearchParams();
  const [ref, setRef] = useState("");
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [marking, setMarking] = useState(false);
  const [resendForm, setResendForm] = useState({ booking_id: "", email: "" });
  const [resendStatus, setResendStatus] = useState("");
  const [cancelForm, setCancelForm] = useState({ booking_id: "", email: "" });
  const [cancelStatus, setCancelStatus] = useState("");

  const verify = async (value) => {
    const id = cleanRef(value ?? ref);
    if (!id) return;
    setChecking(true);
    setResult(null);
    try {
      const r = await axios.get(`${API}/bookings/verify/${id}`);
      setResult({ ok: true, booking: r.data.booking });
    } catch {
      setResult({ ok: false });
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    const q = params.get("ref");
    if (q) { setRef(q); verify(q); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markEntered = async () => {
    setMarking(true);
    try {
      await axios.post(`${API}/bookings/${cleanRef(ref)}/enter`);
    } catch { /* cancelled bookings are rejected server-side */ }
    await verify();
    setMarking(false);
  };

  const resend = async (e) => {
    e.preventDefault();
    setResendStatus("");
    try { await axios.post(`${API}/bookings/resend`, resendForm); } catch { /* generic message either way */ }
    setResendStatus("If the reference and email match our records, the e-ticket has been sent.");
  };

  const cancel = async (e) => {
    e.preventDefault();
    setCancelStatus("");
    try { await axios.post(`${API}/bookings/cancel`, cancelForm); } catch { /* generic message either way */ }
    setCancelStatus("If the reference and email match our records, the booking has been cancelled and its seats released.");
  };

  const b = result?.booking;
  const cancelled = b?.status === "cancelled";

  return <div className="site-shell">
    <header className="topbar"><div className="topbar-inner">
      <Link className="brand" to="/" data-testid="verify-brand-link"><img src="https://tt-devasthanam-booking-349466.hostingersite.com/ttd-logo.png" alt="TT Devasthanam" /><span><b>TT Devasthanam</b><small>Independent booking site</small></span></Link>
      <Link className="secure-chip verify-back" to="/" data-testid="back-home-link"><ArrowLeft size={15} /> Back to booking</Link>
    </div></header>
    <main className="verify-page">
      <p className="eyebrow"><span className="eyebrow-line" /> Entry desk tools</p>
      <h1 className="verify-title">Verify &amp; resend<br /><em>e-tickets.</em></h1>

      <div className="verify-card" data-testid="verify-card">
        <h3>Verify a ticket</h3>
        <p>Paste the Group Booking ID or the scanned QR content to confirm a ticket is genuine.</p>
        <form onSubmit={(e) => { e.preventDefault(); verify(); }}>
          <label>Group Booking ID or QR content<input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="ED0510261946809280" data-testid="verify-ref-input" /></label>
          <button className="primary-button form-submit" type="submit" disabled={checking} data-testid="verify-submit-button">{checking ? "Checking…" : "Verify ticket"}</button>
        </form>
        {result && (result.ok ? (
          cancelled ? (
            <div className="verify-result bad" data-testid="verify-result">
              <h4><Ban size={18} /> Cancelled ticket</h4>
              <p>This booking was cancelled{ b.cancelled_at ? ` on ${fmtTime(b.cancelled_at)}` : "" }. Entry is not permitted.</p>
            </div>
          ) : (
            <div className="verify-result ok" data-testid="verify-result">
              <h4><ShieldCheck size={18} /> Genuine ticket</h4>
              <p>Booking verified — entry permitted for the listed pilgrims after photo ID check.</p>
              {b.payment_verified
                ? <p className="pay-badge ok" data-testid="payment-verified-badge"><BadgeCheck size={14} /> Payment verified</p>
                : <p className="pay-badge pending" data-testid="payment-verified-badge"><Clock3 size={14} /> Payment proof under review</p>}
              <table><tbody>
                <tr><td>Group Booking ID</td><td><strong>{b.booking_id}</strong></td></tr>
                <tr><td>Seva</td><td>{b.service}</td></tr>
                <tr><td>Visit date</td><td>{b.date}</td></tr>
                {b.slot ? <tr><td>Slot</td><td>{b.slot} hrs</td></tr> : null}
                <tr><td>Pilgrims</td><td>{b.pilgrim_details?.length
                  ? b.pilgrim_details.map((p) => `${p.name} (${p.gender}/${p.age}, ${p.id_type} ${p.id_number})`).join(" · ")
                  : b.name}</td></tr>
              </tbody></table>
              {b.entered_at ? (
                <p className="entered-flag" data-testid="entered-flag"><DoorOpen size={14} /> Already entered at {fmtTime(b.entered_at)} — do not re-admit on the same ticket.</p>
              ) : (
                <button className="secondary-button enter-btn" onClick={markEntered} disabled={marking} data-testid="mark-entered-button">
                  <DoorOpen size={15} /> {marking ? "Marking…" : "Mark as entered"}
                </button>
              )}
            </div>
          )
        ) : (
          <div className="verify-result bad" data-testid="verify-result">
            <h4><ShieldX size={18} /> Ticket not found</h4>
            <p>No genuine booking matches this reference. Please do not allow entry.</p>
          </div>
        ))}
      </div>

      <div className="verify-card" data-testid="resend-card">
        <h3>Resend my e-ticket</h3>
        <p>Lost your ticket? Enter the Group Booking ID and the email used during booking.</p>
        <form onSubmit={resend}>
          <label>Group Booking ID<input value={resendForm.booking_id} onChange={(e) => setResendForm({ ...resendForm, booking_id: e.target.value })} placeholder="ED0510261946809280" data-testid="resend-ref-input" /></label>
          <label>Email address<input type="email" value={resendForm.email} onChange={(e) => setResendForm({ ...resendForm, email: e.target.value })} placeholder="Email used during booking" data-testid="resend-email-input" /></label>
          <button className="primary-button form-submit" type="submit" data-testid="resend-submit-button">Email my e-ticket</button>
        </form>
        {resendStatus && <p className="email-status ok resend-status" data-testid="resend-status"><Mail size={14} /> {resendStatus}</p>}
      </div>

      <div className="verify-card" data-testid="cancel-card">
        <h3>Cancel a booking</h3>
        <p>Plans changed? Cancel with your Group Booking ID and booking email — your seats are released immediately for other pilgrims.</p>
        <form onSubmit={cancel}>
          <label>Group Booking ID<input value={cancelForm.booking_id} onChange={(e) => setCancelForm({ ...cancelForm, booking_id: e.target.value })} placeholder="ED0510261946809280" data-testid="cancel-ref-input" /></label>
          <label>Email address<input type="email" value={cancelForm.email} onChange={(e) => setCancelForm({ ...cancelForm, email: e.target.value })} placeholder="Email used during booking" data-testid="cancel-email-input" /></label>
          <button className="primary-button form-submit danger" type="submit" data-testid="cancel-submit-button">Cancel booking</button>
        </form>
        {cancelStatus && <p className="email-status resend-status" data-testid="cancel-status"><Ban size={14} /> {cancelStatus}</p>}
      </div>
    </main>
    <WhatsAppButton />
  </div>;
}
